import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import AppLayout from '../components/AppLayout';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { useAuth } from '../contexts/AuthContext';
import type { OrganogramNode, OrganogramView } from '../types';
import OrganogramFlow from '../components/OrganogramFlow';
import UserContactCard from '../components/UserContactCard';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad } from '../utils/userFacingCopy';

const VIEW_LABELS: Record<OrganogramView, string> = {
  team: 'My Team',
  department: 'My Department',
  businessunit: 'My Business Unit',
  all: 'All',
};

interface UserContactDetails {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
  roleTemplateName?: string;
  managerName?: string;
  departmentName?: string;
  businessUnitName?: string;
  companyName?: string;
}

export default function OrganogramPage() {
  const { viewType } = useParams<{ viewType: OrganogramView }>();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [nodes, setNodes] = useState<OrganogramNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [contactCardOpen, setContactCardOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserContactDetails | null>(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);

  useEffect(() => {
    const loadOrganogram = async () => {
      if (!viewType || !['team', 'department', 'businessunit', 'all'].includes(viewType)) {
        setError('Invalid view type');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        
        const response = await apiService.get<OrganogramNode[]>(
          API_ENDPOINTS.USERS.ORGANOGRAM(viewType)
        );
        
        setNodes(response.data);
      } catch (err: unknown) {
        console.error('Failed to load organogram:', err);
        if (err && typeof err === 'object' && 'response' in err) {
          const axiosError = err as { response?: { status?: number; data?: { message?: string } } };
          if (axiosError.response?.status === 401) {
            navigate('/login');
            return;
          }
          setError(getApiErrorMessage(err, couldNotLoad('the organogram')));
        } else {
          setError(couldNotLoad('the organogram'));
        }
      } finally {
        setLoading(false);
      }
    };

    loadOrganogram();
  }, [viewType, navigate]);

  const handleNodeClick = (nodeId: string) => {
    // Show dialog immediately (non-blocking)
    setContactCardOpen(true);
    setLoadingUserDetails(true);
    setError('');
    setSelectedUser(null);
    
    // If clicking on own profile, we can show it in the contact card with special styling
    // No need to navigate to profile page - user can see their info in the contact card

    // Load user details asynchronously (don't await - let it run in background)
    (async () => {
      try {
        const userResponse = await apiService.get<{
          id: string;
          firstName: string;
          lastName: string;
          email: string;
          roles: string[];
          roleTemplateId?: string;
          managerId?: string;
          departmentNames?: string[];
          businessUnitName?: string;
          companyName?: string;
          // Handle both camelCase and PascalCase for compatibility
          DepartmentNames?: string[];
          BusinessUnitName?: string;
          CompanyName?: string;
        }>(API_ENDPOINTS.USERS.BY_ID(nodeId));

        const user = userResponse.data;
        const departmentNames = user.departmentNames ?? user.DepartmentNames ?? [];
        const departmentName = departmentNames.length > 0 ? departmentNames.join(', ') : undefined;
        const businessUnitName = user.businessUnitName ?? user.BusinessUnitName;
        const companyName = user.companyName ?? user.CompanyName;

        // Load role template name if we have roleTemplateId
        let roleTemplateName: string | undefined;
        if (user.roleTemplateId) {
          try {
            const roleResponse = await apiService.get<{ name: string }>(
              `${API_ENDPOINTS.ROLES.BASE}/${user.roleTemplateId}`
            );
            roleTemplateName = roleResponse.data.name;
          } catch (err) {
            console.warn('Failed to load role template:', err);
          }
        }

        // Load manager name if we have managerId
        let managerName: string | undefined;
        if (user.managerId) {
          try {
            const managerResponse = await apiService.get<{
              firstName: string;
              lastName: string;
            }>(API_ENDPOINTS.USERS.BY_ID(user.managerId));
            managerName = `${managerResponse.data.firstName} ${managerResponse.data.lastName}`;
          } catch (err) {
            console.warn('Failed to load manager:', err);
          }
        }

        setSelectedUser({
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          roles: user.roles,
          roleTemplateName: roleTemplateName,
          managerName: managerName,
          departmentName,
          businessUnitName,
          companyName,
        });
      } catch (err: unknown) {
        console.error('Failed to load user details:', err);
        setContactCardOpen(false);
        if (err && typeof err === 'object' && 'response' in err) {
          const axiosError = err as { response?: { status?: number; data?: { message?: string } } };
          if (axiosError.response?.status === 403) {
            setError('You do not have permission to view this user\'s details.');
          } else {
            setError(getApiErrorMessage(err, couldNotLoad('user details')));
          }
        } else {
          setError(couldNotLoad('user details'));
        }
      } finally {
        setLoadingUserDetails(false);
      }
    })();
  };

  const viewLabel = viewType ? VIEW_LABELS[viewType] : 'Organogram';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !contactCardOpen) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <AccountTreeIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
            {viewLabel}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          View the organisational structure showing reporting relationships, team hierarchies, and department memberships across the organisation.
        </Alert>

        {nodes.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No organisational data available for this view.
            </Typography>
          </Paper>
        ) : (
          <OrganogramFlow nodes={nodes} onNodeClick={handleNodeClick} viewType={viewType} currentUserId={authUser?.id} />
        )}

        <UserContactCard
          open={contactCardOpen}
          onClose={() => {
            setContactCardOpen(false);
            setSelectedUser(null);
            setError('');
          }}
          user={selectedUser}
          loading={loadingUserDetails}
        />
      </Box>
    </AppLayout>
  );
}
